/**
 * A person's own AI keys (ChatGPT, Claude).
 *
 * The key is kept on the server, encrypted (migration 982): the browser saves it
 * once with set_my_ai_provider_key and never reads it back. What the browser
 * knows is which providers have a key and the key's last four characters,
 * loaded at sign-in (AuthSessionSync → loadMyAiKeys). A request on a person's
 * key goes through {@link personalAiFetch}, which posts it to the ai-proxy edge
 * function with the sign-in token; the function attaches the key.
 *
 * Keys saved in this browser's localStorage before migration 982 are moved to
 * the server the first time the person signs in, then removed from the browser.
 *
 * A developer's VITE_OPENAI_API_KEY / VITE_ANTHROPIC_API_KEY still works on
 * their own machine (import.meta.env.DEV only), through the same-origin routes.
 * A VITE_ variable is copied into the files every visitor downloads, so it is
 * never read in a production build. The deployment's own keys live on the
 * server (api/llm, api/openai, api/anthropic).
 *
 * Active provider: only a provider with a key can be active.
 */

import { useSyncExternalStore } from 'react';
import { isFeatureEnabled, FEATURE_FLAGS } from './featureFlags';
import { getSupabaseClient, isSupabaseConfigured } from './supabaseClient';

export type AIProvider = 'openai' | 'anthropic';

/** The person's preferred provider (not a secret; stays in the browser). */
export const AI_PROVIDER_KEY = 'ai_provider';

/** Where keys lived before migration 982; read only to move them to the server. */
const LEGACY_KEY_STORAGE: Record<AIProvider, string> = {
  openai: 'openai_api_key',
  anthropic: 'anthropic_api_key',
};

const DEFAULT_AI_PROVIDER: AIProvider = 'openai';

export interface SavedAiKey {
  last4: string;
  updatedAt: string;
}

export interface MyAiKeysState {
  /** True once the saved keys have been read for the signed-in person. */
  loaded: boolean;
  saved: Partial<Record<AIProvider, SavedAiKey>>;
}

let state: MyAiKeysState = { loaded: false, saved: {} };
const listeners = new Set<() => void>();

function setState(next: MyAiKeysState) {
  state = next;
  listeners.forEach((l) => l());
}

function readLegacyKey(provider: AIProvider): string | null {
  try {
    return localStorage.getItem(LEGACY_KEY_STORAGE[provider])?.trim() || null;
  } catch {
    return null;
  }
}

function removeLegacyKey(provider: AIProvider) {
  try {
    localStorage.removeItem(LEGACY_KEY_STORAGE[provider]);
  } catch {
    /* storage unavailable */
  }
}

/**
 * Read the signed-in person's saved keys (provider and last four only). First
 * moves any key this browser still holds from before migration 982 to the
 * server, and removes it from the browser once the server has it.
 */
export async function loadMyAiKeys(): Promise<void> {
  if (!isSupabaseConfigured()) return;
  const supabase = getSupabaseClient();
  for (const provider of ['openai', 'anthropic'] as const) {
    const legacy = readLegacyKey(provider);
    if (!legacy) continue;
    const { error } = await supabase.rpc('set_my_ai_provider_key', { p_provider: provider, p_key: legacy });
    // A key the server refuses as malformed is not worth keeping either.
    if (!error || error.code === '22023') removeLegacyKey(provider);
  }
  const { data, error } = await supabase.from('user_ai_provider_keys').select('provider, key_last4, updated_at');
  if (error) return;
  const saved: MyAiKeysState['saved'] = {};
  for (const row of (data ?? []) as Array<{ provider: AIProvider; key_last4: string; updated_at: string }>) {
    saved[row.provider] = { last4: row.key_last4, updatedAt: row.updated_at };
  }
  setState({ loaded: true, saved });
}

/** Signed out: forget what this browser knew about the last person's keys. */
export function forgetMyAiKeys(): void {
  setState({ loaded: false, saved: {} });
}

/** Save (or replace) the signed-in person's key. Returns an error message, or null. */
export async function saveMyAiKey(provider: AIProvider, key: string): Promise<string | null> {
  const { data, error } = await getSupabaseClient().rpc('set_my_ai_provider_key', {
    p_provider: provider,
    p_key: key.trim(),
  });
  if (error) return error.message;
  const row = data as { key_last4: string; updated_at: string };
  setState({ loaded: true, saved: { ...state.saved, [provider]: { last4: row.key_last4, updatedAt: row.updated_at } } });
  return null;
}

/** Remove the signed-in person's key. Returns an error message, or null. */
export async function removeMyAiKey(provider: AIProvider): Promise<string | null> {
  const { error } = await getSupabaseClient().rpc('clear_my_ai_provider_key', { p_provider: provider });
  if (error) return error.message;
  const saved = { ...state.saved };
  delete saved[provider];
  setState({ loaded: true, saved });
  return null;
}

/** The saved-keys state, re-rendering when a key is saved, removed or loaded. */
export function useMyAiKeys(): MyAiKeysState {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
  );
}

function devEnvKey(provider: AIProvider): string | undefined {
  if (!import.meta.env.DEV) return undefined;
  const key = provider === 'openai'
    ? (import.meta.env.VITE_OPENAI_API_KEY as string | undefined)
    : (import.meta.env.VITE_ANTHROPIC_API_KEY as string | undefined);
  return key?.trim() || undefined;
}

export function hasSavedAiKey(provider: AIProvider): boolean {
  return !!state.saved[provider];
}

export function providerHasApiKey(provider: AIProvider): boolean {
  return hasSavedAiKey(provider) || !!devEnvKey(provider);
}

/** "…abcd" for a saved key; a note for a developer's env key; null when none. */
export function aiKeyPreview(provider: AIProvider): string | null {
  const saved = state.saved[provider];
  if (saved) return `…${saved.last4}`;
  if (devEnvKey(provider)) return 'Key set via environment (not editable here)';
  return null;
}

/** Where the Anthropic key in use comes from (helps explain a billing error). */
export function getAnthropicKeySource(): 'saved' | 'env' | null {
  if (hasSavedAiKey('anthropic')) return 'saved';
  if (devEnvKey('anthropic')) return 'env';
  return null;
}

const DEV_ROUTE: Record<AIProvider, string> = {
  openai: '/api/openai/v1/chat/completions',
  anthropic: '/api/anthropic/v1/messages',
};

/**
 * Send a request to OpenAI (chat completions) or Anthropic (messages) on the
 * person's own key. The answer is the provider's, status and body.
 */
export async function personalAiFetch(
  provider: AIProvider,
  body: unknown,
  opts?: { signal?: AbortSignal; anthropicVersion?: string },
): Promise<Response> {
  if (hasSavedAiKey(provider)) {
    const { data } = await getSupabaseClient().auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error('Sign in to use your own AI key.');
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? '';
    return fetch(`${supabaseUrl}/functions/v1/ai-proxy`, {
      method: 'POST',
      signal: opts?.signal,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY ?? '',
      },
      body: JSON.stringify({ provider, body, anthropicVersion: opts?.anthropicVersion }),
    });
  }
  const envKey = devEnvKey(provider);
  if (!envKey) throw new Error(`No ${provider === 'openai' ? 'OpenAI' : 'Claude'} API key`);
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (provider === 'openai') headers.Authorization = `Bearer ${envKey}`;
  else {
    headers['x-api-key'] = envKey;
    headers['anthropic-version'] = opts?.anthropicVersion ?? '2023-06-01';
  }
  return fetch(DEV_ROUTE[provider], { method: 'POST', signal: opts?.signal, headers, body: JSON.stringify(body) });
}

/**
 * True when whiteboard photo import may run on our own Claude key (S-32): the
 * build says the deployment offers it (VITE_ANTHROPIC_MANAGED=true, alongside
 * the server's ANTHROPIC_API_KEY and WHITEBOARD_MANAGED_KEY_ENABLED=true) and the
 * whiteboard_managed_key feature flag is on (off by default). The server serves
 * only whiteboard photo reads on that key (api/_lib/managedWhiteboard.js).
 */
export function isManagedAnthropicEnabled(): boolean {
  return (
    (import.meta.env.VITE_ANTHROPIC_MANAGED as string | undefined) === 'true' &&
    isFeatureEnabled(FEATURE_FLAGS.WHITEBOARD_MANAGED_KEY)
  );
}

/** Raw preference from localStorage (may point at a provider without a key). */
export function getStoredAIProvider(): AIProvider {
  const stored = localStorage.getItem(AI_PROVIDER_KEY);
  if (stored === 'openai' || stored === 'anthropic') return stored;
  return DEFAULT_AI_PROVIDER;
}

/**
 * Resolved active provider — never returns a provider without an API key.
 * Falls back to the other provider when the stored preference has no key.
 * Syncs localStorage to match; clears the preference when no keys exist.
 */
export function resolveAIProvider(): AIProvider | null {
  const stored = getStoredAIProvider();
  if (providerHasApiKey(stored)) return stored;

  if (providerHasApiKey('openai')) {
    localStorage.setItem(AI_PROVIDER_KEY, 'openai');
    return 'openai';
  }
  if (providerHasApiKey('anthropic')) {
    localStorage.setItem(AI_PROVIDER_KEY, 'anthropic');
    return 'anthropic';
  }
  // Keys not read yet: keep the preference for when they are.
  if (state.loaded) localStorage.removeItem(AI_PROVIDER_KEY);
  return null;
}

/** @alias resolveAIProvider */
export function getAIProvider(): AIProvider | null {
  return resolveAIProvider();
}

/** Set active provider only when that provider has an API key. */
export function setAIProvider(provider: AIProvider): boolean {
  if (!providerHasApiKey(provider)) return false;
  localStorage.setItem(AI_PROVIDER_KEY, provider);
  return true;
}

/**
 * How whiteboard (and other Claude-vision) features will authenticate.
 * Prefer the person's own key when present; otherwise use the workspace
 * managed key when the deployment enables it.
 */
export type AnthropicAuthMode = 'byok' | 'workspace' | 'none';

export function resolveAnthropicAuthMode(): AnthropicAuthMode {
  if (providerHasApiKey('anthropic')) return 'byok';
  if (isManagedAnthropicEnabled()) return 'workspace';
  return 'none';
}

/** True when photo analysis can run (own key or workspace managed Claude). */
export function canUseAnthropicVision(): boolean {
  return resolveAnthropicAuthMode() !== 'none';
}
